import React, { useState, useRef, useEffect } from "react";
import { Share2, Instagram, MessageCircle, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";

const INSTAGRAM_HANDLE = "https://www.instagram.com/umaang.co";

/**
 * ShareStoryMenu — small share button + popover for a success story card.
 * Behaviour:
 *   - On mobile (navigator.share available) → opens native share sheet (Instagram, WhatsApp, etc.)
 *   - On desktop → shows a small popover with Instagram / WhatsApp / Copy-link options.
 *     Instagram doesn't accept URL-based sharing from the web, so we copy the caption to
 *     clipboard AND open @umaang.co Instagram so the user can paste as a post/story.
 */
export default function ShareStoryMenu({ story, className = "" }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!story) return null;

  const landingUrl = typeof window !== "undefined"
    ? `${window.location.origin}/?story=${story.id || ""}`
    : "https://www.umaang.co/";
  const caption = `${story.couple_names} found their forever on Umaang 💛\n\n${(story.story || "").slice(0, 200)}${(story.story || "").length > 200 ? "…" : ""}\n\nRead more: ${landingUrl}\n\n#Umaang #RealRishtas #SuccessStory`;

  const openNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${story.couple_names} on Umaang`, text: caption, url: landingUrl });
        return true;
      }
    } catch {/* user cancelled */}
    return false;
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    const shared = await openNative();
    if (!shared) setOpen((v) => !v);
  };

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success("Caption copied — paste it in your Instagram post"); }
    catch { toast.error("Couldn't copy — please try again"); }
  };

  const shareInstagram = async () => {
    await copyCaption();
    window.open(INSTAGRAM_HANDLE, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(caption)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(landingUrl); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — please try again"); }
    setOpen(false);
  };

  return (
    <div ref={menuRef} className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        data-testid={`share-story-btn-${story.id || ""}`}
        onClick={handleClick}
        aria-label="Share this story"
        className="w-9 h-9 rounded-full grid place-items-center bg-white/95 backdrop-blur shadow-lg hover:scale-110 transition-transform"
      >
        <Share2 className="w-4 h-4 text-[#C85A53]" strokeWidth={2} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-30 min-w-[220px] bg-white border shadow-2xl overflow-hidden"
          style={{ borderRadius: 12, borderColor: "var(--umg-border)" }}
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--umg-border)" }}>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Share story</div>
            <button aria-label="Close" onClick={() => setOpen(false)} className="text-[#57534E] hover:text-black">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            data-testid="share-instagram"
            onClick={shareInstagram}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAF7F2] transition text-left"
          >
            <div className="w-8 h-8 rounded-lg grid place-items-center text-white" style={{ background: "linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)" }}>
              <Instagram className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">Instagram</div>
              <div className="text-[11px] text-[#57534E]">Copies caption &amp; opens app</div>
            </div>
          </button>
          <button
            data-testid="share-whatsapp"
            onClick={shareWhatsApp}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAF7F2] transition text-left"
          >
            <div className="w-8 h-8 rounded-lg grid place-items-center text-white" style={{ background: "#25D366" }}>
              <MessageCircle className="w-4 h-4" fill="white" />
            </div>
            <div>
              <div className="text-sm font-semibold">WhatsApp</div>
              <div className="text-[11px] text-[#57534E]">Share directly in a chat</div>
            </div>
          </button>
          <button
            data-testid="share-copy-link"
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAF7F2] transition text-left border-t"
            style={{ borderColor: "var(--umg-border)" }}
          >
            <div className="w-8 h-8 rounded-lg grid place-items-center text-white bg-[#374151]">
              <Copy className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">Copy link</div>
              <div className="text-[11px] text-[#57534E]">Paste anywhere</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
